"use client";

import { type ReactNode, useCallback, useEffect, useState } from "react";

import { clsx } from "../../utils/clsx";
import { transformChildrenToString } from "../../utils/transform-children-to-text";
import { Toast } from "./toast";
import { ToastContextProvider, type ToastDetails } from "./toast-context";
import { useResumableTimeouts } from "./use-resumable-timeouts";

interface ToastProviderProps {
  className?: string;
  children?: ReactNode;
}

interface ToastEntry {
  id: string;
  details: ToastDetails;
  closing?: boolean;
}

const defaultToastDurations: Record<ToastDetails["color"], number> = {
  blue: 5000,
  malachite: 2000,
  yellow: 5000,
  red: Number.POSITIVE_INFINITY,
};

export const ToastProvider = ({ className, children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const [isHoveringOver, setIsHoveringOver] = useState(false);
  const [isPageInvisible, setIsPageInvisible] = useState(false);

  const { clearTimeout, setTimeout, pauseTimeouts, resumeTimeouts } = useResumableTimeouts();

  useEffect(() => {
    const handler = () => {
      setIsPageInvisible(document.visibilityState === "hidden");
    };

    document.addEventListener("visibilitychange", handler);

    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  useEffect(() => {
    if (!isHoveringOver && !isPageInvisible) {
      resumeTimeouts();
    } else {
      pauseTimeouts();
    }
  }, [isHoveringOver, isPageInvisible, pauseTimeouts, resumeTimeouts]);

  const closeToast = useCallback(
    (id: string) => {
      setToasts((toasts) => toasts.map((toast) => (toast.id === id ? { ...toast, closing: true } : toast)));
      clearTimeout(id);

      window.setTimeout(() => {
        setToasts((toasts) => toasts.filter((toast) => toast.id !== id));
      }, 500);
    },
    [clearTimeout],
  );

  const openToast = useCallback(
    (details: ToastDetails) => {
      const newToast: ToastEntry = {
        id: details.id ?? transformChildrenToString(details.children),
        details,
      };

      const duration = details.duration ?? defaultToastDurations[details.color];

      setToasts((toasts) => {
        const existingToastIndex = toasts.findIndex(
          (toast) =>
            toast.id === newToast.id ||
            transformChildrenToString(toast.details.children) === transformChildrenToString(newToast.details.children),
        );

        if (existingToastIndex >= 0) {
          return toasts.map((toast, index) => (index === existingToastIndex ? newToast : toast));
        }

        return [newToast, ...toasts];
      });

      if (Number.isFinite(duration)) {
        setTimeout(newToast.id, () => closeToast(newToast.id), duration);
      }
    },
    [setTimeout, closeToast],
  );

  return (
    <ToastContextProvider value={{ openToast, closeToast }}>
      {children}

      <div
        className={clsx("pointer-events-none fixed right-0 bottom-0 left-0 z-40 flex flex-col-reverse items-center", className)}
        onMouseEnter={() => setIsHoveringOver(true)}
        onMouseLeave={() => setIsHoveringOver(false)}
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            details={toast.details}
            stage={toast.closing ? "closing" : "opening"}
            onClose={() => closeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContextProvider>
  );
};
